/**
 * AccountDetail — /accounts/:accountsId page.
 *
 * Header + tabs + right sidebar. Tab visibility depends on the account's
 * lifecycle phase:
 *   - Activity, Notes, Proposals, Documents → always visible
 *   - RFC → always visible. The tab hosts the "Generate RFC" action, so an
 *     admin can generate the Request for Coverage from the very first stage
 *     (the RFC step is generated deliberately here, never reached by skipping).
 *   - MSA → visible from Proposal phase onward
 *   - Members / Plans → Active phase only (the Payroll tab is hidden until the
 *     payroll file validator is finished)
 *   - Group Info → visible in any phase once the account has a group (this is
 *     where a manually-inserted account gets linked to an existing VBA group)
 */

import React, {useCallback, useEffect, useState} from 'react';
import {useParams, useSearchParams} from 'react-router-dom';
import type {AccountsSubStage} from '@api/generated/client';
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@common-web/components/ui/tabs';
import {cn} from '@common-web/lib/utils';
import {useIsInternal} from '@common-web/providers/portalProvider';
import {trpc} from '@common-web/lib/trpc';
import {useAuthStore} from '@common-web/stores/authStore';
import {ACCOUNTS_SUB_STAGES} from '@common/accountsView';
import {CODE_PERMISSIONS} from '@common/permissions';
import {usePermissions} from '@common-web/hooks/use-permissions';
import {
  resolveBrokerApprovalEnabled,
  resolveInternalAdvancedAccountDetailEnabled,
  resolveLinkVbaGroupEnabled,
  resolveSwotCallEnabled,
  type InternalFeaturesShape,
} from '@common/menuFlags';
import {AccountHeader} from './components/AccountHeader';
import {MarkStatusModal, type MarkStatusTarget} from './components/MarkStatusModal';
import {AccountLifecycleSection} from './components/AccountLifecycleSection';
import {MoveLifecycleModal, type MoveConfirmDetails} from './components/MoveLifecycleModal';
import {AccountRfcWizard} from './components/AccountRfcWizard';
import {AccountSidebar} from './components/AccountSidebar';
import {
  GenerateProposalModal,
  type ProposalGenerationType,
} from './components/GenerateProposalModal';
import {OverviewTab} from './tabs/OverviewTab';
import {ActivityTab} from './tabs/ActivityTab';
import {NotesTab} from './tabs/NotesTab';
import {ProposalsTab} from './tabs/ProposalsTab';
import {DocumentsTab} from './tabs/DocumentsTab';
import {MsaTab} from './tabs/MsaTab';
import {GroupInfoTab} from './tabs/GroupInfoTab';
import {RfcTab} from './tabs/RfcTab';
import {GroupPlansTab} from '../client/GroupPlansTab';
import {SendToClientModal} from './components/SendToClientModal';
interface AccountDetailProps {
  routePrefix?: string;
}

// MSA is available from Onboarding onward (Onboarding → Implementation →
// Active). Proposals now live in the Prospect phase, which has no MSA step.
const MSA_VISIBLE_PHASES: ReadonlyArray<string> = ['ONBOARDING', 'IMPLEMENTATION', 'ACTIVE'];

/**
 * The internal portal defaults to the basic CBS-style detail view (Overview ·
 * Documents · Invoicing · Commissions · Activity, notes inline on Overview, no
 * sidebar); the org-wide `advancedAccountDetail` flag opts back into the full
 * page. Resolved before rendering the page so it never flashes the wrong
 * variant. Client/broker portals skip the query and always get the full page.
 */
export function AccountDetail({routePrefix = '/accounts'}: AccountDetailProps): React.ReactElement {
  const isInternal = useIsInternal();
  const internalConfig = trpc.internal.config.get.useQuery(undefined, {
    enabled: isInternal,
    staleTime: 60_000,
  });

  if (isInternal && internalConfig.isLoading) {
    return <div className='p-8 text-muted-foreground'>Loading account…</div>;
  }

  const basic =
    isInternal &&
    !resolveInternalAdvancedAccountDetailEnabled(
      (internalConfig.data?.features ?? {}) as InternalFeaturesShape,
    );

  return <AccountDetailInner routePrefix={routePrefix} basic={basic} />;
}

interface AccountDetailInnerProps {
  routePrefix: string;
  /** Render the basic CBS-style variant (internal portal with the flag off). */
  basic: boolean;
}

function AccountDetailInner({routePrefix, basic}: AccountDetailInnerProps): React.ReactElement {
  const {accountsId} = useParams<{accountsId: string}>();
  const utils = trpc.useUtils();
  const isInternal = useIsInternal();
  // Internal portal renders cbs-portal's underline tab bar (transparent list
  // over a hairline border, active tab = orange text + 2px orange underline).
  // Client portals keep the boxed shadcn tabs.
  const tabsListClass = isInternal
    ? 'flex h-auto w-full justify-start gap-0 rounded-none border-b bg-transparent p-0'
    : 'flex w-full';
  const tabTriggerClass = isInternal
    ? '-mb-px rounded-none border-b-2 border-transparent bg-transparent px-4 py-2.5 text-[13px] font-medium text-muted-foreground shadow-none transition-colors data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:font-semibold data-[state=active]:text-primary data-[state=active]:shadow-none'
    : 'flex-1';
  const [searchParams, setSearchParams] = useSearchParams();
  // Initialize the tab from `?tab=` so note-added notifications can deep-link
  // straight to the Notes tab. Only a known set is honored; anything else
  // falls back to the Overview tab. The basic variant honors only its five
  // tabs — note deep-links land on Overview, where notes render inline.
  const ALLOWED_TABS = React.useMemo(
    () =>
      basic
        ? new Set(['overview', 'documents', 'activity'])
        : new Set([
            'overview',
            'activity',
            'notes',
            'proposals',
            'documents',
            'rfc',
            'msa',
            'products',
            'group-info',
          ]),
    [basic],
  );
  const [activeTab, setActiveTabState] = React.useState(() => {
    const qp = searchParams.get('tab');
    return qp && ALLOWED_TABS.has(qp) ? qp : 'overview';
  });
  const setActiveTab = React.useCallback(
    (next: string) => {
      // Basic variant: targets outside its tab set (e.g. a next action steering
      // toward the MSA tab) land on Overview instead of a blank pane.
      const resolved = ALLOWED_TABS.has(next) ? next : 'overview';
      setActiveTabState(resolved);
      setSearchParams(
        (prev) => {
          if (resolved === 'overview') {
            prev.delete('tab');
          } else {
            prev.set('tab', resolved);
          }
          return prev;
        },
        {replace: true},
      );
    },
    [setSearchParams, ALLOWED_TABS],
  );
  const [rfcWizardOpen, setRfcWizardOpen] = React.useState(false);
  // Move-group modal (the one surface for manual lifecycle moves). `target`
  // preselects a destination — the approve CTA opens it aimed at the RFC gate.
  const [moveModal, setMoveModal] = React.useState<{
    open: boolean;
    target: AccountsSubStage | null;
  }>({open: false, target: null});
  const openMoveModal = React.useCallback(
    (target: AccountsSubStage | null = null) => setMoveModal({open: true, target}),
    [],
  );
  const [proposalGeneratorOpen, setProposalGeneratorOpen] = React.useState(false);
  // Generate-proposal modal (post-RFC-approval Proposal step) + the type preset
  // carried into the Proposals tab generator (driven by `?generate=` in the new
  // tab opened on "Generate").
  const [generateModalOpen, setGenerateModalOpen] = React.useState(false);
  const [proposalGenType, setProposalGenType] = React.useState<ProposalGenerationType | undefined>(
    undefined,
  );
  const [sendToClientOpen, setSendToClientOpen] = React.useState(false);
  // Latest plaintext password from regeneratePassword (the stored one is hashed
  // and can't be revealed), shown in the Send-to-client modal.
  const [sharePassword, setSharePassword] = React.useState<string | null>(null);
  const clientProfile = useAuthStore((state) => state.user?.clientProfile);
  const permissions = usePermissions();
  const brokerProfile = useAuthStore((state) => state.user?.brokerProfile);
  // Client admin for this tenant with the tenant-level opt-in flag enabled may
  // adopt an existing VBA group into the account (drives the Group Info tab CTA).
  const canLinkVbaGroup =
    !!clientProfile &&
    permissions.has(CODE_PERMISSIONS.accounts.admin.key) &&
    resolveLinkVbaGroupEnabled(clientProfile?.features);
  // Managing the open enrollment window is admin-only, mirroring the server's
  // `isAccountsAdmin`: cross-tenant internal users (tenant.access_all) or
  // tenant users holding `accounts.admin`. Brokers and non-admin client users
  // still see the window and the header badge, read-only.
  const cbsInternalProfile = useAuthStore((state) => state.user?.cbsInternalProfile);
  const canEditOpenEnrollment = cbsInternalProfile
    ? permissions.has(CODE_PERMISSIONS.tenant.accessAll.key)
    : !!clientProfile && permissions.has(CODE_PERMISSIONS.accounts.admin.key);
  // SWOT call (Synrgy-only) prospect sub-stage — gated by the tenant feature
  // flag, resolved from whichever profile the viewer holds.
  const swotEnabled = resolveSwotCallEnabled(clientProfile?.features ?? brokerProfile?.features);
  // Whether this tenant's onboarding pipeline includes the broker-approval gate.
  // Drives the submitted-RFC next action: client-only approval ("Review and
  // approve") with a broker waiting badge when off.
  const brokerApprovalEnabled = resolveBrokerApprovalEnabled(
    clientProfile?.features ?? brokerProfile?.features,
  );

  // Opening the proposal generator from the Overview tab switches to the
  // Proposals tab (which hosts the generator dialog) and opens it there. Clear
  // any one-shot `?generate=` preset so a manual open starts on the default flow
  // rather than a stale Quick Proposal / Informed Analysis preset.
  const openProposalGenerator = React.useCallback(() => {
    setProposalGenType(undefined);
    setActiveTab('proposals');
    setProposalGeneratorOpen(true);
  }, [setActiveTab]);

  // The `?generate=` preset is one-shot: once the generator closes, drop it so
  // any subsequent (manual) open falls back to the default flow.
  const handleProposalGeneratorOpenChange = React.useCallback((open: boolean) => {
    setProposalGeneratorOpen(open);
    if (!open) setProposalGenType(undefined);
  }, []);

  // "Generate" from the modal opens the Proposals tab in a NEW browser tab with
  // the chosen type preset (`?generate=`), leaving the current tab where it is.
  const handleGenerateProposal = React.useCallback((type: ProposalGenerationType) => {
    setGenerateModalOpen(false);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', 'proposals');
    url.searchParams.set('generate', type);
    window.open(url.toString(), '_blank', 'noopener');
  }, []);

  // When this page loads with `?generate=`, jump to the Proposals tab with the
  // generator open and the type preset, then strip the param so a refresh
  // doesn't re-trigger it. This is the receiving end of `handleGenerateProposal`.
  const generateParam = searchParams.get('generate');
  useEffect(() => {
    // Basic variant has no Proposals tab — leave the param alone so switching
    // the flag back on can still honor the link.
    if (basic) return;
    if (generateParam !== 'QUICK_PROPOSAL' && generateParam !== 'INFORMED_ANALYSIS') return;
    setProposalGenType(generateParam);
    setActiveTabState('proposals');
    setProposalGeneratorOpen(true);
    setSearchParams(
      (prev) => {
        prev.set('tab', 'proposals');
        prev.delete('generate');
        return prev;
      },
      {replace: true},
    );
  }, [basic, generateParam, setSearchParams]);

  const query = trpc.account.getById.useQuery(
    {accountsId: accountsId ?? ''},
    {enabled: !!accountsId},
  );

  // ALLOWED_TABS is static, but several tabs are additionally gated on the
  // loaded account (phase, linked group, virtual VBA card). A deep link to a
  // gated-off tab (e.g. `?tab=msa` on a pre-Onboarding account) would leave the
  // controlled Tabs value with no matching trigger or content — a blank pane —
  // so reset to Overview once the account shows the tab isn't rendered. The
  // conditions mirror the trigger/content gates in the JSX below.
  const accountForTabGating = query.data;
  useEffect(() => {
    if (basic || !accountForTabGating) return;
    const isVirtualAccount = accountForTabGating.isVbaGroup;
    const hasGroup = !!accountForTabGating.group;
    const tabAvailable: Record<string, boolean> = {
      proposals: !isVirtualAccount,
      rfc: !isVirtualAccount,
      msa: MSA_VISIBLE_PHASES.includes(accountForTabGating.lifecyclePhase) && !isVirtualAccount,
      products: hasGroup,
      'group-info': (hasGroup || canLinkVbaGroup) && !isVirtualAccount,
    };
    if (activeTab in tabAvailable && !tabAvailable[activeTab]) {
      setActiveTab('overview');
    }
  }, [basic, accountForTabGating, activeTab, canLinkVbaGroup, setActiveTab]);

  // Unread note-added notifications for this account drive the red dot on the
  // Notes tab; opening the tab marks them read so the indicator clears.
  const noteNotificationsUnreadQuery = trpc.user.notification.getUnreadCount.useQuery(
    {accountsId: accountsId ?? ''},
    {enabled: !!accountsId},
  );
  const markNoteNotificationsReadMutation = trpc.user.notification.markAllAsRead.useMutation({
    onSuccess: () => {
      noteNotificationsUnreadQuery.refetch();
      utils.user.notification.getUnreadCount.invalidate();
    },
  });
  const noteUnreadCount = noteNotificationsUnreadQuery.data?.count ?? 0;

  // Basic variant renders notes inline on Overview, so that's where unread
  // note notifications get marked read.
  const notesVisibleOnTab = basic ? 'overview' : 'notes';
  useEffect(() => {
    if (!accountsId) return;
    if (activeTab !== notesVisibleOnTab) return;
    if (noteUnreadCount === 0) return;
    markNoteNotificationsReadMutation.mutate({accountsId});
  }, [activeTab, accountsId, noteUnreadCount, notesVisibleOnTab]);

  // "Mark as On Hold" / "Mark as Lost" confirmation modal — each collects a
  // required reason recorded in the account history.
  const [markStatusTarget, setMarkStatusTarget] = useState<MarkStatusTarget | null>(null);
  const unarchive = trpc.account.unarchive.useMutation({
    onSuccess: () => utils.account.getById.invalidate({accountsId: accountsId ?? ''}),
  });
  const markLost = trpc.account.markLost.useMutation({
    onSuccess: () => {
      setMarkStatusTarget(null);
      utils.account.getById.invalidate({accountsId: accountsId ?? ''});
    },
    // Keep the modal open on failure so the reason isn't lost on a retry.
    onError: (err) => alert(err.message),
  });
  const invalidateAccount = useCallback(() => {
    utils.account.getById.invalidate({accountsId: accountsId ?? ''});
    utils.account.getActivity.invalidate({accountsId: accountsId ?? ''});
    utils.account.list.invalidate();
    // The approval next actions also transition the linked legacy RfcSubmission
    // (status, history) — refresh the RFC tab's detail query so its review
    // card and History sub-tab don't go stale.
    utils.clients.rfc.getById.invalidate();
  }, [utils, accountsId]);

  const transitionSubStage = trpc.account.transitionSubStage.useMutation({
    onSuccess: invalidateAccount,
    onError: (err) => alert(err.message),
  });
  const skipSubStage = trpc.account.skipSubStage.useMutation({
    onSuccess: invalidateAccount,
    onError: (err) => alert(err.message),
  });
  const submitRfc = trpc.account.submit.useMutation({
    onSuccess: invalidateAccount,
    onError: (err) => alert(err.message),
  });
  const adjudicate = trpc.account.adjudicate.useMutation({
    onSuccess: invalidateAccount,
    onError: (err) => alert(err.message),
  });
  const brokerApprove = trpc.account.brokerApprove.useMutation({
    onSuccess: invalidateAccount,
    onError: (err) => alert(err.message),
  });
  const submitToCbs = trpc.account.submitToCbs.useMutation({
    onSuccess: invalidateAccount,
    onError: (err) => alert(err.message),
  });
  const beginImplementation = trpc.account.beginImplementation.useMutation({
    onSuccess: invalidateAccount,
    onError: (err) => alert(err.message),
  });
  const advanceCbsStage = trpc.account.advanceCbsStage.useMutation({
    onSuccess: invalidateAccount,
    onError: (err) => alert(err.message),
  });
  // Analyst correction of a lifecycle step's auto-stamped date (internal only —
  // the band only surfaces the edit control for internal admins).
  const setStageDate = trpc.account.setStageDate.useMutation({
    onSuccess: invalidateAccount,
    onError: (err) => alert(err.message),
  });
  // The Move-group modal's "MSA Package Sent" hand-off: confirming it sends the
  // actual DocuSign package (which is what advances the account to MSA Sent).
  const sendMsa = trpc.account.sendMsa.useMutation({
    onSuccess: invalidateAccount,
    onError: (err) => alert(err.message),
  });
  const regeneratePassword = trpc.account.regeneratePassword.useMutation({
    onSuccess: (result) => setSharePassword(result.plaintextPassword),
    onError: (err) => alert(err.message),
  });

  const requestSwotCall = trpc.account.requestSwotCall.useMutation({
    onSuccess: invalidateAccount,
    onError: (err) => alert(err.message),
  });
  const skipSwotCall = trpc.account.skipSwotCall.useMutation({
    onSuccess: invalidateAccount,
    onError: (err) => alert(err.message),
  });
  const requestSkipSwotCall = trpc.account.requestSkipSwotCall.useMutation({
    onSuccess: invalidateAccount,
    onError: (err) => alert(err.message),
  });
  const scheduleSwotCall = trpc.account.scheduleSwotCall.useMutation({
    onSuccess: invalidateAccount,
    onError: (err) => alert(err.message),
  });
  const completeSwotCall = trpc.account.completeSwotCall.useMutation({
    onSuccess: invalidateAccount,
    onError: (err) => alert(err.message),
  });
  const cancelSwotCall = trpc.account.cancelSwotCall.useMutation({
    onSuccess: invalidateAccount,
    onError: (err) => alert(err.message),
  });

  const nextActionPending =
    transitionSubStage.isPending ||
    skipSubStage.isPending ||
    submitRfc.isPending ||
    adjudicate.isPending ||
    brokerApprove.isPending ||
    submitToCbs.isPending ||
    beginImplementation.isPending ||
    advanceCbsStage.isPending ||
    requestSwotCall.isPending ||
    skipSwotCall.isPending ||
    requestSkipSwotCall.isPending ||
    scheduleSwotCall.isPending ||
    completeSwotCall.isPending ||
    cancelSwotCall.isPending;

  if (!accountsId) {
    return <div className='p-8'>Invalid account ID.</div>;
  }
  if (query.isLoading) {
    return <div className='p-8 text-muted-foreground'>Loading account…</div>;
  }
  if (query.error || !query.data) {
    return (
      <div className='p-8 text-red-600'>
        Account not found{query.error ? `: ${query.error.message}` : ''}.
      </div>
    );
  }

  const account = query.data;
  // Live VBA groups without an Accounts row render as read-only virtual
  // accounts (`vba:` ids): same page, but pipeline artifacts (notes, documents,
  // proposals, RFC, MSA) can't be written and lifecycle actions are hidden.
  const isVirtual = account.isVbaGroup;
  // Group Info is the home for the "Link existing VBA group" action, so it shows
  // once the account has a group OR the caller can link one (any lifecycle phase).
  const showGroupInfo = (!!account.group || canLinkVbaGroup) && !isVirtual;
  // Products (plans) require a group to attach to; surface the tab once the
  // account has one, in any lifecycle phase.
  const showProducts = !!account.group;
  // The RFC tab is always available so the RFC can be generated (and the access
  // link / password shared) from the very first stage. When no RFC exists yet
  // the tab renders its "Generate RFC" empty state. On the normal path,
  // generating the RFC is side-effect-free — it no longer advances the account
  // into Onboarding (moving forward stays a deliberate transition, see
  // skipSubStage gating). The one exception is the skipped-SWOT LEAD case: an
  // account still on the SWOT Call step with swotStatus SKIPPED has no
  // intermediate step to land on, so generating the RFC advances it to RFC Draft
  // (DRAFT / ONBOARDING).
  const hasRfc = !!account.rfcSubmission;
  const showRfc = !isVirtual;
  const showMsa = MSA_VISIBLE_PHASES.includes(account.lifecyclePhase) && !isVirtual;
  // Whether an MSA actually went out (a DocuSign envelope exists or the
  // legacy/manual `msa` record is set). An account can sit on MSA_SENT with
  // nothing sent — Synrgy's proposal save/skip lands directly there — in which
  // case the header keeps showing the "Go to MSA" next action.
  const msaSent = (account.msaEnvelopes?.length ?? 0) > 0 || !!account.msa;

  // Send-to-client modal inputs: the RFC access link and the account's primary
  // contact (contacts are returned primary-first).
  const primaryContact = account.contacts?.[0] ?? null;
  const contactName = primaryContact
    ? [primaryContact.firstName, primaryContact.lastName].filter(Boolean).join(' ').trim() || null
    : null;
  const rfcAccessUrl = account.rfcSubmission
    ? `${window.location.origin}/rfc/${account.rfcSubmission.accessToken}`
    : '';

  // Total FT+PT employees from the RFC group-info form (draft data can hold
  // null mid-fill despite the schema, hence the runtime typeof guard). Feeds
  // the Overview's Plan Year & Coverage card.
  const rfcFormTotalEmployees =
    account.rfcSubmission?.section125FormData?.groupInfo?.totalEmployees ??
    account.rfcSubmission?.mecFormData?.groupInfo?.totalEmployees;
  const rfcTotalEmployees =
    typeof rfcFormTotalEmployees === 'number' && Number.isFinite(rfcFormTotalEmployees)
      ? rfcFormTotalEmployees
      : null;

  const nextActionHandlers = {
    onGenerateProposal: openProposalGenerator,
    onGenerateProposalModal: () => setGenerateModalOpen(true),
    // PROPOSAL isn't in the transition input enum and is never a next-action
    // target — narrow accordingly.
    onTransitionSubStage: (targetSubStage: typeof account.subStage) =>
      transitionSubStage.mutate({
        accountsId: account.id,
        targetSubStage: targetSubStage as Exclude<AccountsSubStage, 'PROPOSAL'>,
      }),
    onGenerateRfc: () => setRfcWizardOpen(true),
    onSubmitRfc: () => submitRfc.mutate({accountsId: account.id}),
    onSendToClient: () => setSendToClientOpen(true),
    onClientApprove: () => {
      // RFC gate — approving carries the account past the RFC, so it requires
      // an RFC PROVIDED (any PDF on file or the digital form filled in). When
      // there's none yet, open the Move-group modal aimed at the gate so the
      // admin can upload/fill it, then move forward (mirrors synrgyinfinity).
      if (account.rfcProvided) {
        adjudicate.mutate({accountsId: account.id, decision: 'APPROVE'});
      } else {
        openMoveModal('CLIENT_APPROVED');
      }
    },
    onBrokerApprove: () => brokerApprove.mutate({accountsId: account.id}),
    onSendMsa: () => setActiveTab('msa'),
    onSubmitToCbs: () => submitToCbs.mutate({accountsId: account.id}),
    onBeginImplementation: () => beginImplementation.mutate({accountsId: account.id}),
    onAdvanceCbs: (
      targetSubStage:
        | 'WELCOME_CALL'
        | 'CENSUS_BUILD_OUT'
        | 'CENSUS_REVIEW'
        | 'CENSUS_UPDATE_NEEDED'
        | 'CENSUS_APPROVED'
        | 'IMPLEMENTATION_CALL'
        | 'DRY_RUN_1'
        | 'FINANCIAL_CALL'
        | 'DRY_RUN_2'
        | 'GO_LIVE_RUN',
    ) => advanceCbsStage.mutate({accountsId: account.id, targetSubStage}),
  };

  // Confirm a move from the Move-group modal. "MSA Sent" is never a raw stage
  // set — it's reached by actually sending the MSA package, so confirming it
  // parks the account at MSA Pending (crossing the RFC gate through the gated
  // transition when needed), fires the DocuSign send, and lands on the MSA tab
  // showing the envelopes (or, on a failed send, the send section to finish
  // from — fix the contact email, send remaining, or submit manually).
  //
  // `details` carries the forward move's effective date and one resolution
  // (date or skip-with-reason) per passed-over stage — the server rejects the
  // jump without them, so passed-over steps are never completed silently.
  const handleMoveConfirm = async (
    target: AccountsSubStage,
    details: MoveConfirmDetails,
  ): Promise<void> => {
    const resolutionInputs = {
      stageResolutions: details.stageResolutions.length > 0 ? details.stageResolutions : undefined,
      effectiveDate: details.effectiveDate ?? undefined,
    };
    try {
      if (target === 'MSA_SENT') {
        const order = ACCOUNTS_SUB_STAGES as readonly string[];
        if (order.indexOf(account.subStage) < order.indexOf('MSA_PENDING')) {
          try {
            // The modal computed the resolutions against MSA Pending (the raw
            // transition target of this hand-off), so they ride this hop.
            await transitionSubStage.mutateAsync({
              accountsId: account.id,
              targetSubStage: 'MSA_PENDING',
              ...resolutionInputs,
            });
          } catch {
            // Transition failed (error surfaced via the mutation's onError
            // alert) — the account never reached an MSA-visible phase, so
            // close the modal without landing on a tab that isn't rendered.
            setMoveModal({open: false, target: null});
            return;
          }
        }
        try {
          await sendMsa.mutateAsync({accountsId: account.id});
        } finally {
          // The account now sits at/past MSA Pending, so the MSA tab is
          // visible. Send errors are surfaced via the mutation's onError
          // alert; land on the tab either way so the outcome (or what's left
          // to do) is visible.
          setMoveModal({open: false, target: null});
          setActiveTab('msa');
        }
        return;
      }
      // PROPOSAL isn't in the transition input enum (and editableSubStagesFor
      // derives from it), so the modal can never offer it — narrow accordingly.
      await transitionSubStage.mutateAsync({
        accountsId: account.id,
        targetSubStage: target as Exclude<AccountsSubStage, 'PROPOSAL'>,
        ...resolutionInputs,
      });
      setMoveModal({open: false, target: null});
    } catch {
      // Error already surfaced via the mutation's onError alert — keep the
      // modal open so the admin can adjust the destination.
    }
  };

  // "Fill out digital form" from the Move-group modal — the structured
  // alternative to the plain PDF drop: the RFC tab when an RFC exists,
  // otherwise the Generate-RFC wizard.
  const handleFillRfc = (): void => {
    setMoveModal({open: false, target: null});
    if (hasRfc) {
      setActiveTab('rfc');
    } else {
      setRfcWizardOpen(true);
    }
  };

  const swotHandlers = {
    onRequest: () => requestSwotCall.mutate({accountsId: account.id}),
    onSkip: () => skipSwotCall.mutate({accountsId: account.id}),
    onRequestSkip: () => requestSkipSwotCall.mutate({accountsId: account.id}),
    onSchedule: (scheduledDate: string) =>
      scheduleSwotCall.mutate({accountsId: account.id, scheduledDate}),
    onComplete: () => completeSwotCall.mutate({accountsId: account.id}),
    onCancel: () => cancelSwotCall.mutate({accountsId: account.id}),
  };

  return (
    <div className='flex h-full flex-col overflow-hidden'>
      <div className='shrink-0 px-6 pt-6'>
        <AccountHeader
          account={account}
          routePrefix={routePrefix}
          hasRfc={hasRfc}
          msaSent={msaSent}
          onUnarchive={() => unarchive.mutate({accountsId: account.id})}
          onMarkOnHold={() => setMarkStatusTarget('ON_HOLD')}
          onMarkLost={() => setMarkStatusTarget('REJECTED')}
          onEditLifecycle={() => openMoveModal(null)}
          onSkipSubStage={(targetSubStage, reason) =>
            // The band's skip target is the next configured sub-stage, which
            // is never PROPOSAL (not in the skip input enum) — narrow.
            skipSubStage.mutate({
              accountsId: account.id,
              targetSubStage: targetSubStage as Exclude<AccountsSubStage, 'PROPOSAL'>,
              reason,
            })
          }
          isTransitioning={transitionSubStage.isPending || skipSubStage.isPending}
          editableSubStages={account._canTransition?.editableSubStages ?? []}
          nextActionHandlers={nextActionHandlers}
          brokerApprovalEnabled={brokerApprovalEnabled}
          welcomeCallEnabled={account.welcomeCallEnabled}
          onEditStageDate={(subStage, isoDate) =>
            setStageDate.mutate({
              accountsId: account.id,
              subStage,
              date: isoDate ? new Date(isoDate) : null,
            })
          }
          isNextActionPending={nextActionPending}
          swotEnabled={swotEnabled}
          swotHandlers={swotHandlers}
          readOnly={isVirtual}
        />
      </div>

      <div className='flex min-h-0 flex-1 overflow-hidden'>
        <div className='min-w-0 flex-1 overflow-y-auto px-6 py-4'>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className={tabsListClass}>
              {/* Basic variant renders the demo's five V1 tabs. The full page
                  leads with the demo's Overview → Documents → Activity order on
                  the internal portal; client portals keep the original order. */}
              {(basic
                ? ['overview', 'documents', 'activity']
                : isInternal
                  ? [
                      'overview',
                      'documents',
                      'activity',
                      'notes',
                      'proposals',
                      'rfc',
                      'msa',
                      'products',
                      'group-info',
                    ]
                  : [
                      'overview',
                      'activity',
                      'notes',
                      'proposals',
                      'documents',
                      'rfc',
                      'msa',
                      'products',
                      'group-info',
                    ]
              ).map((tab) => {
                switch (tab) {
                  case 'overview':
                    return (
                      <TabsTrigger key={tab} value='overview' className={tabTriggerClass}>
                        Overview
                      </TabsTrigger>
                    );
                  case 'activity':
                    return (
                      <TabsTrigger key={tab} value='activity' className={tabTriggerClass}>
                        Activity
                      </TabsTrigger>
                    );
                  case 'notes':
                    return (
                      <TabsTrigger
                        key={tab}
                        value='notes'
                        className={cn('relative', tabTriggerClass)}
                      >
                        Notes
                        {noteUnreadCount > 0 && (
                          <span
                            aria-label={`${noteUnreadCount} unread note notification${noteUnreadCount === 1 ? '' : 's'}`}
                            className='ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-bold text-white'
                          >
                            {noteUnreadCount}
                          </span>
                        )}
                      </TabsTrigger>
                    );
                  case 'proposals':
                    return isVirtual ? null : (
                      <TabsTrigger key={tab} value='proposals' className={tabTriggerClass}>
                        Proposals
                      </TabsTrigger>
                    );
                  case 'documents':
                    return (
                      <TabsTrigger key={tab} value='documents' className={tabTriggerClass}>
                        Documents
                      </TabsTrigger>
                    );
                  case 'rfc':
                    return showRfc ? (
                      <TabsTrigger key={tab} value='rfc' className={tabTriggerClass}>
                        RFC
                      </TabsTrigger>
                    ) : null;
                  case 'msa':
                    return showMsa ? (
                      <TabsTrigger key={tab} value='msa' className={tabTriggerClass}>
                        MSA
                      </TabsTrigger>
                    ) : null;
                  case 'products':
                    return showProducts ? (
                      <TabsTrigger key={tab} value='products' className={tabTriggerClass}>
                        Products
                      </TabsTrigger>
                    ) : null;
                  case 'group-info':
                    return showGroupInfo ? (
                      <TabsTrigger key={tab} value='group-info' className={tabTriggerClass}>
                        Group Info
                      </TabsTrigger>
                    ) : null;
                  default:
                    return null;
                }
              })}
            </TabsList>

            <TabsContent value='overview' className='mt-4'>
              {/* Internal portal mounts the lifecycle tracker here as a
                  cbs-portal-style card — Overview tab only, in every phase:
                  groups move in and out of Active, so the band stays visible
                  (and editable) after go-live, defaulting to its collapsed
                  one-line summary once Active. Hidden only for read-only
                  virtual VBA accounts, which have no pipeline to work. Client
                  portals render it in the header instead. */}
              {isInternal && !isVirtual ? (
                <div className='mb-5 rounded-xl border bg-card p-5'>
                  <AccountLifecycleSection
                    account={account}
                    hasRfc={hasRfc}
                    msaSent={msaSent}
                    onEditLifecycle={() => openMoveModal(null)}
                    isTransitioning={transitionSubStage.isPending || skipSubStage.isPending}
                    onSkipSubStage={(targetSubStage, reason) =>
                      // Same narrowing as the header's skip handler above.
                      skipSubStage.mutate({
                        accountsId: account.id,
                        targetSubStage: targetSubStage as Exclude<AccountsSubStage, 'PROPOSAL'>,
                        reason,
                      })
                    }
                    editableSubStages={account._canTransition?.editableSubStages ?? []}
                    nextActionHandlers={nextActionHandlers}
                    brokerApprovalEnabled={brokerApprovalEnabled}
                    welcomeCallEnabled={account.welcomeCallEnabled}
                    onEditStageDate={(subStage, isoDate) =>
                      setStageDate.mutate({
                        accountsId: account.id,
                        subStage,
                        date: isoDate ? new Date(isoDate) : null,
                      })
                    }
                    isNextActionPending={nextActionPending}
                    swotEnabled={swotEnabled}
                    swotHandlers={swotHandlers}
                  />
                </div>
              ) : null}
              <OverviewTab
                accountsId={account.id}
                primaryContact={primaryContact}
                onGenerateProposal={openProposalGenerator}
                showProposals={!basic && !isVirtual}
                readOnly={isVirtual}
                rfcInternal={account.rfcSubmission?.internalData ?? null}
                rfcTotalEmployees={rfcTotalEmployees}
                account={{
                  companyName: account.companyName,
                  dba: account.group?.metadata?.dba ?? account.dba ?? null,
                  lifecyclePhase: account.lifecyclePhase,
                  subStage: account.subStage,
                  viewStageKey: account.viewStageKey,
                  viewSubStageKey: account.viewSubStageKey,
                  state: account.state,
                  estimatedFtEmployees: account.estimatedFtEmployees,
                  estimatedPtEmployees: account.estimatedPtEmployees,
                  estimatedStartDate: account.estimatedStartDate,
                  group: account.group
                    ? {
                        id: account.group.id,
                        estimatedMemberCount: account.group.estimatedMemberCount,
                        groupType: account.group.groupType,
                        metadata: account.group.metadata,
                      }
                    : null,
                }}
              />
              {/* Basic variant mirrors the demo Overview: products and notes
                  render inline below the summary cards (no Products/Notes tab). */}
              {basic ? (
                <div className='mt-5 space-y-5'>
                  {account.group ? (
                    <GroupPlansTab groupId={account.group.id} title='Products' />
                  ) : null}
                  <NotesTab accountsId={account.id} readOnly={isVirtual} />
                </div>
              ) : null}
            </TabsContent>
            <TabsContent value='activity' className='mt-4'>
              <ActivityTab accountsId={account.id} />
            </TabsContent>
            {!basic ? (
              <TabsContent value='notes' className='mt-4'>
                <NotesTab accountsId={account.id} readOnly={isVirtual} />
              </TabsContent>
            ) : null}
            {!basic && !isVirtual ? (
              <TabsContent value='proposals' className='mt-4'>
                <ProposalsTab
                  accountsId={account.id}
                  generatorOpen={proposalGeneratorOpen}
                  onGeneratorOpenChange={handleProposalGeneratorOpenChange}
                  initialType={proposalGenType}
                />
              </TabsContent>
            ) : null}
            <TabsContent value='documents' className='mt-4'>
              <DocumentsTab
                accountsId={account.id}
                // Basic variant has no RFC tab to jump to.
                onNavigateToRfc={basic || isVirtual ? undefined : () => setActiveTab('rfc')}
                readOnly={isVirtual}
              />
            </TabsContent>
            {showRfc && !basic ? (
              <TabsContent value='rfc' className='mt-4'>
                <RfcTab accountsId={account.id} />
              </TabsContent>
            ) : null}
            {showMsa && !basic ? (
              <TabsContent value='msa' className='mt-4'>
                <MsaTab
                  accountsId={account.id}
                  companyName={account.companyName}
                  msa={account.msa}
                  msaEnvelopes={account.msaEnvelopes}
                  subStage={account.subStage}
                  lifecyclePhase={account.lifecyclePhase}
                />
              </TabsContent>
            ) : null}
            {showProducts && account.group && !basic ? (
              <TabsContent value='products' className='mt-4'>
                <GroupPlansTab groupId={account.group.id} title='Products' />
              </TabsContent>
            ) : null}
            {showGroupInfo && !basic ? (
              <TabsContent value='group-info' className='mt-4'>
                <GroupInfoTab
                  accountsId={account.id}
                  companyName={account.companyName}
                  group={account.group ?? null}
                  canLinkVbaGroup={canLinkVbaGroup}
                  canEditOpenEnrollment={canEditOpenEnrollment}
                />
              </TabsContent>
            ) : null}
          </Tabs>
        </div>

        {basic ? null : (
          <AccountSidebar
            accountsId={account.id}
            readOnly={isVirtual}
            account={{
              companyName: account.companyName,
              lifecyclePhase: account.lifecyclePhase,
              subStage: account.subStage,
              viewStageKey: account.viewStageKey,
              viewSubStageKey: account.viewSubStageKey,
              state: account.state,
              websiteUrl: account.websiteUrl,
              estimatedFtEmployees: account.estimatedFtEmployees,
              estimatedPtEmployees: account.estimatedPtEmployees,
              estimatedStartDate: account.estimatedStartDate,
              averageEmployeeSalary: account.averageEmployeeSalary?.toString() ?? null,
              assignedBroker: account.assignedBroker,
              group: account.group
                ? {
                    id: account.group.id,
                    estimatedMemberCount: account.group.estimatedMemberCount,
                  }
                : null,
              contacts: account.contacts,
            }}
          />
        )}
      </div>

      <MarkStatusModal
        target={markStatusTarget}
        companyName={account.companyName}
        confirming={markLost.isPending}
        onCancel={() => setMarkStatusTarget(null)}
        onConfirm={(reason) => {
          if (!markStatusTarget) return;
          markLost.mutate({accountsId: account.id, reason: markStatusTarget, note: reason});
        }}
      />

      <MoveLifecycleModal
        open={moveModal.open}
        onOpenChange={(open) => setMoveModal((s) => ({...s, open}))}
        accountsId={account.id}
        companyName={account.companyName}
        currentSubStage={account.subStage}
        editableSubStages={
          account.isArchived ? [] : (account._canTransition?.editableSubStages ?? [])
        }
        rfcProvided={account.rfcProvided}
        initialTarget={moveModal.target}
        isTransitioning={transitionSubStage.isPending || sendMsa.isPending}
        onConfirm={handleMoveConfirm}
        onFillRfc={handleFillRfc}
        welcomeCallEnabled={account.welcomeCallEnabled}
      />

      <AccountRfcWizard
        account={{
          id: account.id,
          companyName: account.companyName,
          state: account.state,
          estimatedFtEmployees: account.estimatedFtEmployees,
          estimatedPtEmployees: account.estimatedPtEmployees,
          assignedBroker: account.assignedBroker,
          swotStatus: account.swotStatus,
          lifecyclePhase: account.lifecyclePhase,
        }}
        open={rfcWizardOpen}
        onOpenChange={setRfcWizardOpen}
        onGenerated={() => setActiveTab('rfc')}
      />

      <GenerateProposalModal
        open={generateModalOpen}
        onOpenChange={setGenerateModalOpen}
        companyName={account.companyName}
        onGenerate={handleGenerateProposal}
      />

      <SendToClientModal
        open={sendToClientOpen}
        onOpenChange={setSendToClientOpen}
        accessUrl={rfcAccessUrl}
        companyName={account.companyName}
        contactName={contactName}
        password={sharePassword}
        onGeneratePassword={() => regeneratePassword.mutate({accountsId: account.id})}
        isGeneratingPassword={regeneratePassword.isPending}
      />
    </div>
  );
}
